import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import GestionTorneo from "./components/GestionTorneo";
import AdminLogin from "./components/AdminLogin";
import SuperLigaVista from "./components/SuperLigaVista";
import EditableImage from "./components/EditableImage";
import PublicidadesCarrusel from "./components/PublicidadesCarrusel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [imageOverrides, setImageOverrides] = useState({});
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [visitas, setVisitas] = useState(0);
  const [hasEntered, setHasEntered] = useState(false);
  const [showSuperLiga, setShowSuperLiga] = useState(false);
  const [showCampeonatos, setShowCampeonatos] = useState(false);
  const [showPartidosEnVivo, setShowPartidosEnVivo] = useState(false);

  const fetchImageOverrides = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/imagenes`);
      setImageOverrides(r.data || {});
    } catch (err) {
      console.error('Error al cargar imagenes override:', err);
    }
  }, []);

  const handleImageUpdated = useCallback((key, data) => {
    setImageOverrides(prev => {
      const next = { ...prev };
      if (data && data.url) next[key] = data.url;
      else delete next[key];
      return next;
    });
  }, []);

  const initializeData = useCallback(async () => {
    try {
      await axios.post(`${API}/initialize-all`);
    } catch (error) {
      console.error('Error al inicializar:', error);
    }
  }, []);

  const incrementarVisitas = useCallback(async () => {
    try {
      await axios.post(`${API}/visitas/incrementar`);
    } catch (error) {
      console.error('Error al incrementar visitas:', error);
    }
  }, []);

  const fetchVisitas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/visitas`);
      setVisitas(response.data.visitas);
    } catch (error) {
      console.error('Error al obtener visitas:', error);
    }
  }, []);

  useEffect(() => {
    const path = window.location.pathname.toLowerCase();
    const hash = window.location.hash.toLowerCase();
    
    if (path.includes('/copa-tribu') || hash.includes('#copa-tribu')) {
      setHasEntered(true);
      setShowSuperLiga(false);
      setShowCampeonatos(false);
      setShowPartidosEnVivo(false);
    } else if (path.includes('/superliga') || hash.includes('#superliga')) {
      setShowSuperLiga(true);
      setHasEntered(false);
      setShowCampeonatos(false);
      setShowPartidosEnVivo(false);
    } else if (path.includes('/campeonatos') || hash.includes('#campeonatos')) {
      setShowCampeonatos(true);
      setHasEntered(false);
      setShowSuperLiga(false);
      setShowPartidosEnVivo(false);
    } else if (path.includes('/en-vivo') || hash.includes('#en-vivo')) {
      setShowPartidosEnVivo(true);
      setHasEntered(false);
      setShowSuperLiga(false);
      setShowCampeonatos(false);
    }
    
    initializeData();
    incrementarVisitas();
    fetchVisitas();
    fetchImageOverrides();
  }, [initializeData, incrementarVisitas, fetchVisitas, fetchImageOverrides]);

  const handleAdminLoginSuccess = (password) => {
    setIsAdmin(true);
    setAdminPassword(password || '');
    setShowAdminLogin(false);
  };

  const handleAdminLogout = () => {
    setIsAdmin(false);
    setAdminPassword('');
  };

  // Funciones de navegación con actualización de URL
  const navegarACopaTirbu = () => {
    window.history.pushState({}, '', '#copa-tribu');
    setHasEntered(true);
    setShowSuperLiga(false);
    setShowCampeonatos(false);
    setShowPartidosEnVivo(false);
  };

  const navegarASuperLiga = () => {
    window.history.pushState({}, '', '#superliga');
    setShowSuperLiga(true);
    setHasEntered(false);
    setShowCampeonatos(false);
    setShowPartidosEnVivo(false);
  };

  const navegarACampeonatos = () => {
    window.history.pushState({}, '', '#campeonatos');
    setShowCampeonatos(true);
    setHasEntered(false);
    setShowSuperLiga(false);
    setShowPartidosEnVivo(false);
  };

  const navegarAPartidosEnVivo = () => {
    window.history.pushState({}, '', '#en-vivo');
    setShowPartidosEnVivo(true);
    setHasEntered(false);
    setShowSuperLiga(false);
    setShowCampeonatos(false);
  };

  const volverAlInicio = () => {
    window.history.pushState({}, '', '/');
    setHasEntered(false);
    setShowSuperLiga(false);
    setShowCampeonatos(false);
    setShowPartidosEnVivo(false);
  };

  return (
    <div className="App">
      {/* 1. Portada con contador de visitas */}
      <header className="header-portada">
        <EditableImage
          imageKey="portada_header"
          defaultSrc="https://customer-assets.emergentagent.com/job_futbolcopa/artifacts/2ax5usc2_1000982242.jpg"
          alt="Copa Tribu Apertura"
          className="portada-image"
          isAdmin={isAdmin}
          adminPassword={adminPassword}
          overrides={imageOverrides}
          onUpdated={handleImageUpdated}
        />
        <div className="visitas-overlay">
          👥 Visitas: <span>{visitas}</span>
        </div>
      </header>

      <div className="container">
        {/* Landing Page - ANTES de ingresar */}
        {!hasEntered && !showSuperLiga && !showCampeonatos && !showPartidosEnVivo && (
          <>
            {/* 1. Botón INGRESAR A LA COPA TRIBU */}
            <div className="seccion-ingreso">
              <div className="card">
                <EditableImage
                  imageKey="card_copa_tribu"
                  defaultSrc="https://customer-assets.emergentagent.com/job_futbolcopa/artifacts/0vy12fal_1000982273.jpg"
                  alt="Copa Tribu Infantil 2026"
                  style={{width: '100%', height: 'auto', borderRadius: '10px'}}
                  isAdmin={isAdmin}
                  adminPassword={adminPassword}
                  overrides={imageOverrides}
                  onUpdated={handleImageUpdated}
                />
                <button 
                  className="btn-ingresar-copa"
                  onClick={navegarACopaTirbu}
                  data-testid="btn-ingresar-copa"
                >
                  🏆 INGRESAR A LA COPA TRIBU
                </button>
              </div>
            </div>

            {/* 2. Botón INGRESAR A LA SUPERLIGA */}
            <div className="seccion-ingreso">
              <div className="card">
                <EditableImage
                  imageKey="card_superliga"
                  defaultSrc="https://customer-assets.emergentagent.com/job_8c14b421-4ac9-4715-be06-a1ba975e6adb/artifacts/2mnerwzc_1001160378.jpg"
                  alt="Serie A SuperLiga - Campeonato de Futsal FIFA de Primera División"
                  style={{width: '100%', height: 'auto', borderRadius: '10px'}}
                  isAdmin={isAdmin}
                  adminPassword={adminPassword}
                  overrides={imageOverrides}
                  onUpdated={handleImageUpdated}
                />
                <button 
                  className="btn-ingresar-copa"
                  onClick={navegarASuperLiga}
                  data-testid="btn-ingresar-superliga"
                >
                  🏆 INGRESAR A LA SUPERLIGA
                </button>
              </div>
            </div>

            {/* 3. Botón INGRESAR A CAMPEONATOS NACIONALES */}
            <div className="seccion-ingreso">
              <div className="card">
                <EditableImage
                  imageKey="card_campeonatos"
                  defaultSrc="https://customer-assets.emergentagent.com/job_dd669451-0e43-4ea0-af94-15b40aa6f56a/artifacts/me2mqvot_1000997376.jpg"
                  alt="Campeonatos Nacionales"
                  style={{width: '100%', height: 'auto', borderRadius: '10px'}}
                  isAdmin={isAdmin}
                  adminPassword={adminPassword}
                  overrides={imageOverrides}
                  onUpdated={handleImageUpdated}
                />
                <button 
                  className="btn-ingresar-copa"
                  onClick={navegarACampeonatos}
                  data-testid="btn-ingresar-campeonatos"
                >
                  🏆 INGRESAR A CAMPEONATOS NACIONALES
                </button>
              </div>
            </div>

            {/* 4. Botón INGRESAR A PARTIDOS EN VIVO */}
            <div className="seccion-ingreso">
              <div className="card">
                <EditableImage
                  imageKey="card_envivo"
                  defaultSrc="https://customer-assets.emergentagent.com/job_8c14b421-4ac9-4715-be06-a1ba975e6adb/artifacts/wgo4klc3_1001161375.jpg"
                  alt="Gol Stream - Transmisión en Vivo"
                  style={{width: '100%', height: 'auto', borderRadius: '10px'}}
                  isAdmin={isAdmin}
                  adminPassword={adminPassword}
                  overrides={imageOverrides}
                  onUpdated={handleImageUpdated}
                />
                <button 
                  className="btn-ingresar-copa"
                  onClick={navegarAPartidosEnVivo}
                  data-testid="btn-ingresar-envivo"
                  style={{background: 'linear-gradient(135deg, #FF0000, #CC0000)', color: 'white'}}
                >
                  📺 INGRESAR A PARTIDOS EN VIVO
                </button>
              </div>
            </div>

            {/* 5. Auspiciantes y Redes Sociales */}
            <div className="grid-container">
              <div className="card sponsor-card">
                <EditableImage
                  imageKey="auspiciantes"
                  defaultSrc="https://customer-assets.emergentagent.com/job_dd669451-0e43-4ea0-af94-15b40aa6f56a/artifacts/1rzpv1xe_1000972565.jpg"
                  alt="Auspiciantes"
                  style={{width: '100%', height: 'auto', borderRadius: '10px'}}
                  isAdmin={isAdmin}
                  adminPassword={adminPassword}
                  overrides={imageOverrides}
                  onUpdated={handleImageUpdated}
                />
              </div>

              {/* Redes Sociales */}
              <div className="card social-card">
                <h3 style={{color: '#0055A4', marginBottom: '15px', fontSize: '1.1em'}}>Síguenos en nuestras redes</h3>
                <div className="social-image-wrapper">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_futbolcopa/artifacts/y5fma0oq_1000983894.jpg" 
                    alt="Síguenos en Facebook, WhatsApp e Instagram" 
                    useMap="#social-map"
                    className="social-media-image"
                  />
                  <map name="social-map">
                    <area 
                      shape="rect" 
                      coords="0,0,33%,100%" 
                      href="https://www.facebook.com/share/19aUakbCj6/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      alt="Facebook"
                    />
                    <area 
                      shape="rect" 
                      coords="33%,0,66%,100%" 
                      href="https://whatsapp.com/channel/0029Va8zTG429753JwcJLp3f" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      alt="WhatsApp"
                    />
                    <area 
                      shape="rect" 
                      coords="66%,0,100%,100%" 
                      href="https://www.instagram.com/futsal_fifa_encarnacion?igsh=MnZoNzJpcHhlb3M4" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      alt="Instagram"
                    />
                  </map>
                  <div className="social-clickable-overlay">
                    <a 
                      href="https://www.facebook.com/share/19aUakbCj6/" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-zone facebook-zone"
                      title="Facebook"
                      aria-label="Síguenos en Facebook"
                    />
                    <a 
                      href="https://whatsapp.com/channel/0029Va8zTG429753JwcJLp3f" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-zone whatsapp-zone"
                      title="WhatsApp"
                      aria-label="Contáctanos por WhatsApp"
                    />
                    <a 
                      href="https://www.instagram.com/futsal_fifa_encarnacion?igsh=MnZoNzJpcHhlb3M4" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-zone instagram-zone"
                      title="Instagram"
                      aria-label="Síguenos en Instagram"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Carrusel de publicidades editable por admin */}
            <PublicidadesCarrusel isAdmin={isAdmin} adminPassword={adminPassword} />

            {/* 4. Footer */}
            <footer className="footer">
              <p>Desarrollado por <strong>Agencia Queta®</strong></p>
              <p style={{fontSize: '0.9em', marginTop: '5px'}}>Todos los derechos reservados</p>
              <p style={{fontSize: '0.9em', marginTop: '5px'}}>Encarnación 2026</p>
            </footer>
          </>
        )}

        {/* Sección SuperLiga - Clubes Confirmados */}
        {!hasEntered && showSuperLiga && (
          <div className="superliga-section">
            <button 
              className="btn-volver-menu"
              onClick={volverAlInicio}
              style={{marginBottom: '20px'}}
            >
              ← Volver al Inicio
            </button>
            
            <SuperLigaVista isAdmin={isAdmin} adminPassword={adminPassword} />
          </div>
        )}

        {/* Sección Campeonatos Nacionales */}
        {!hasEntered && showCampeonatos && (
          <div className="campeonatos-section">
            <button 
              className="btn-volver-menu"
              onClick={volverAlInicio}
              style={{marginBottom: '20px'}}
            >
              ← Volver al Inicio
            </button>
            
            <div className="card">
              <h2 style={{textAlign: 'center', color: '#0055A4', marginBottom: '30px', fontSize: '2em'}}>
                🏆 Campeonatos Nacionales
              </h2>
              
              <div style={{textAlign: 'center', padding: '20px'}}>
                <EditableImage
                  imageKey="campeonatos_interior"
                  defaultSrc="https://customer-assets.emergentagent.com/job_dd669451-0e43-4ea0-af94-15b40aa6f56a/artifacts/me2mqvot_1000997376.jpg"
                  alt="Campeonatos Nacionales de Futsal"
                  style={{
                    width: '100%',
                    maxWidth: '800px',
                    height: 'auto',
                    borderRadius: '15px',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                    marginBottom: '40px'
                  }}
                  isAdmin={isAdmin}
                  adminPassword={adminPassword}
                  overrides={imageOverrides}
                  onUpdated={handleImageUpdated}
                />
                
                <h3 style={{
                  color: '#0055A4',
                  marginBottom: '30px',
                  fontSize: '1.8em',
                  fontWeight: 'bold'
                }}>
                  📅 Agenda de Campeonatos 2026
                </h3>
                
                {/* Agenda - Calendario Deportivo 2026 */}
                <div style={{marginBottom: '30px'}}>
                  <EditableImage
                    imageKey="calendario_2026"
                    defaultSrc="https://customer-assets.emergentagent.com/job_dd669451-0e43-4ea0-af94-15b40aa6f56a/artifacts/vnr0ef7h_1000997310.jpg"
                    alt="Calendario Deportivo 2026"
                    style={{
                      width: '100%',
                      maxWidth: '900px',
                      height: 'auto',
                      borderRadius: '15px',
                      boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
                      marginBottom: '20px'
                    }}
                    isAdmin={isAdmin}
                    adminPassword={adminPassword}
                    overrides={imageOverrides}
                    onUpdated={handleImageUpdated}
                  />
                </div>
                
                {/* Agenda - Calendario Deportivo Copafufi */}
                <div style={{marginBottom: '30px'}}>
                  <EditableImage
                    imageKey="calendario_copafufi"
                    defaultSrc="https://customer-assets.emergentagent.com/job_dd669451-0e43-4ea0-af94-15b40aa6f56a/artifacts/7b87uuy2_1000997309.jpg"
                    alt="Calendario Deportivo Nacionales Copafufi 2026"
                    style={{
                      width: '100%',
                      maxWidth: '900px',
                      height: 'auto',
                      borderRadius: '15px',
                      boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)'
                    }}
                    isAdmin={isAdmin}
                    adminPassword={adminPassword}
                    overrides={imageOverrides}
                    onUpdated={handleImageUpdated}
                  />
                </div>
                
                <p style={{
                  color: '#666',
                  fontSize: '1.1em',
                  marginTop: '30px',
                  lineHeight: '1.6',
                  fontStyle: 'italic'
                }}>
                  Consulta las fechas y categorías de los campeonatos nacionales de futsal 2026
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sección Partidos en Vivo - Gol Stream */}
        {!hasEntered && showPartidosEnVivo && (
          <div className="partidos-envivo-section">
            <button 
              className="btn-volver-menu"
              onClick={volverAlInicio}
              style={{marginBottom: '20px'}}
            >
              ← Volver al Inicio
            </button>
            
            <div className="card" style={{background: 'linear-gradient(135deg, #0055A4, #003366)', padding: '30px', textAlign: 'center'}}>
              <EditableImage
                imageKey="envivo_hero"
                defaultSrc="https://customer-assets.emergentagent.com/job_8c14b421-4ac9-4715-be06-a1ba975e6adb/artifacts/wgo4klc3_1001161375.jpg"
                alt="Gol Stream - Transmisión en Vivo"
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  height: 'auto',
                  borderRadius: '15px',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                  marginBottom: '30px'
                }}
                isAdmin={isAdmin}
                adminPassword={adminPassword}
                overrides={imageOverrides}
                onUpdated={handleImageUpdated}
              />
              
              <h2 style={{color: '#FFD700', fontSize: '2em', marginBottom: '15px', textShadow: '2px 2px 4px rgba(0,0,0,0.5)'}}>
                📺 GOL STREAM
              </h2>
              
              <p style={{color: 'white', fontSize: '1.3em', marginBottom: '25px'}}>
                Transmisión en vivo de todos los partidos
              </p>
              
              <a 
                href="https://youtube.com/@golstream1?si=0IbNmalNZv-j68K9"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#FF0000',
                  color: 'white',
                  padding: '15px 40px',
                  borderRadius: '50px',
                  textDecoration: 'none',
                  fontSize: '1.2em',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(255, 0, 0, 0.4)',
                  transition: 'transform 0.3s, box-shadow 0.3s'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 6px 20px rgba(255, 0, 0, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 15px rgba(255, 0, 0, 0.4)';
                }}
              >
                ▶️ VER CANAL EN YOUTUBE
              </a>
              
              <div style={{marginTop: '30px', padding: '20px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px'}}>
                <p style={{color: '#FFD700', fontSize: '1.1em', marginBottom: '10px'}}>
                  🎥 Síguenos para no perderte ningún partido
                </p>
                <p style={{color: 'white', fontSize: '0.95em'}}>
                  Transmitimos en vivo los partidos de Copa Tribu, SuperLiga y más
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Contenido después de ingresar - Gestión del Torneo */}
        {hasEntered && !showSuperLiga && (
          <GestionTorneo 
            isAdmin={isAdmin} 
            onAdminLogout={handleAdminLogout}
            onVolverMenu={volverAlInicio}
          />
        )}
      </div>

      {/* 6. Botón flotante de admin */}
      <button 
        className="admin-float-btn"
        onClick={() => setShowAdminLogin(true)}
        title="Acceso Administrador"
        data-testid="admin-key-btn"
      >
        🔑
      </button>

      {/* Indicador de sesión admin */}
      {isAdmin && (
        <div className="admin-indicator" data-testid="admin-indicator">
          <span>👤 Admin</span>
          <button onClick={handleAdminLogout} className="admin-logout-small" data-testid="admin-logout-indicator-btn">
            🚪
          </button>
        </div>
      )}

      {/* Modal de login admin */}
      {showAdminLogin && (
        <AdminLogin 
          onSuccess={handleAdminLoginSuccess}
          onClose={() => setShowAdminLogin(false)}
        />
      )}
    </div>
  );
}

export default App;